/**
 * mapFocus.ts
 * 地図フォーカス・ズーム制御関連メソッド
 * App.vue から切り出し
 */

import { requireLogin } from '@/js/utils/messageHelper'

type VM = any

export function maybeFocusCenterIncludePoint(vm: VM, aLngLat: number[], bLngLat: number[], opts?: Record<string, any>) {
  if (Date.now() < vm.suppressUntil) return;
  return vm.focusCenterIncludePoint(aLngLat, bLngLat, opts);
}
export function openKyusekiDialog(vm: VM) {
  // ログイン関門（求積表OCR）
  if (!requireLogin({ title: '求積表インポート', icon: 'mdi-table-search' })) return
  vm.onKyusekiDialogVisibilityChange(true)
}
export function onKyusekiDialogVisibilityChange(vm: VM, value: boolean) {
  const target = !!value
  if (vm.showKyusekiDialog === target) {
    vm.$store.dispatch(target ? 'showFloatingWindow' : 'hideFloatingWindow', 'kyuseki-import')
    return
  }
  vm.showKyusekiDialog = target
  vm.$store.dispatch(target ? 'showFloatingWindow' : 'hideFloatingWindow', 'kyuseki-import')
  // ダイアログを閉じる時にinitialFileをクリア
  if (!target) {
    vm.$store.commit('setKyusekiInitialFile', null)
  }
}
export function closeKyusekiWindow(vm: VM) {
  vm.onKyusekiDialogVisibilityChange(false)
}
export function openHandwriteDialog(vm: VM) {
  if (!vm.currentJobId) {
    const dispatched = vm.$store.dispatch('ui/notify', { type: 'info', message: 'ジョブを選択するとJob Pointsへの追加がスムーズになります。' })
    if (dispatched?.catch) dispatched.catch(() => {})
  }
  vm.showHandwriteDialog = true
}
export function onImported(vm: VM, payload: Record<string, any>) {
  // 子コンポーネントからの完了イベントを受け取る
  // 例: { count, crs, area } または { error }
  if (payload?.error) {
    vm.$store.dispatch('ui/notify', { type: 'error', message: `取り込み失敗: ${payload.error}` })
  } else {
    const { count, crs, area } = payload
    vm.$store.dispatch('ui/notify', { type: 'success', message: `取り込み完了: ${count}点 / ${crs} / 面積 ${area?.toFixed?.(3) ?? area} m²` })
  }
}
export function onHandwriteImported(vm: VM, payload: Record<string, any>) {
  if (payload?.error) {
    vm.$store.dispatch('ui/notify', { type: 'error', message: `手書きOCR取り込み失敗: ${payload.error}` })
    return
  }
  if (payload?.count) {
    const message = `手書きOCR: ${payload.count}点をJob Pointsに登録しました。`
    vm.$store.dispatch('ui/notify', { type: 'success', message })
  }
}
export function installTouchSuppressor(vm: VM) {
  const map = (vm.$store && vm.$store.state) ? vm.$store.state.map01 : null;
  if (!map || !map.getCanvasContainer) return;
  const el = map.getCanvasContainer();
  const setSuppress = () => {
    vm.suppressUntil = Date.now() + 20000;
    // alert(vm.suppressUntil);
  };

  // タッチ系の「終了」イベントで20秒抑止
  el.addEventListener('touchend',       setSuppress, { passive: true });
  el.addEventListener('touchcancel',    setSuppress, { passive: true });
  el.addEventListener('pointerup',      setSuppress, { passive: true });
  el.addEventListener('pointercancel',  setSuppress, { passive: true });
  el.addEventListener('gestureend',     setSuppress, { passive: true }); // iOS保険

  // // MapLibre の終了イベントでも抑止
  // map.on('dragend',   setSuppress);
  // map.on('zoomend',   setSuppress);
  // map.on('rotateend', setSuppress);
  // map.on('pitchend',  setSuppress);
}
/**
 * 始点(centerLngLat)を画面中心に固定したまま、
 * 終点(targetLngLat)が画面に収まるようにズーム（必要な時だけズームアウト）
 */
export function focusCenterIncludePoint (vm: VM, centerLngLat: number[], targetLngLat: number[], {
  padding = 60,
  animate = false,
  maxZoom,
  minZoom,
  hysteresis = 0.02   // 0.02 ≒ 2%スケール差 ≒ ズーム差 ~0.03
}: { padding?: number; animate?: boolean; maxZoom?: number; minZoom?: number; hysteresis?: number } = {}) {
  const map = (vm.$store?.state?.map01) || vm.map01 || vm.map
  if (!map || !centerLngLat || !targetLngLat) return

  const cx = Number(centerLngLat[0]), cy = Number(centerLngLat[1])
  const tx = Number(targetLngLat[0]),  ty = Number(targetLngLat[1])
  if (![cx,cy,tx,ty].every(n => Number.isFinite(n))) return

  const cont  = map.getContainer ? map.getContainer() : map.getCanvas?.()
  const w = cont?.clientWidth  ?? 0
  const h = cont?.clientHeight ?? 0
  const halfW = Math.max(0, (w / 2) - padding)
  const halfH = Math.max(0, (h / 2) - padding)
  if (halfW === 0 || halfH === 0) return

  const toMerc = (lng: number, lat: number) => {
    const x = (lng + 180) / 360
    const s = Math.sin(lat * Math.PI / 180)
    const y = (1 - Math.log((1 + s) / (1 - s)) / Math.PI) / 2
    return [x, y]
  }
  const [mxC, myC]   = toMerc(cx, cy)
  let   [mxT, myT]   = toMerc(tx, ty)

  // 経度ラップ（±0.5 世界）
  let dxu = mxT - mxC
  if (dxu >  0.5) { mxT -= 1; dxu = mxT - mxC }
  if (dxu < -0.5) { mxT += 1; dxu = mxT - mxC }
  const dyu = myT - myC

  const EPS = 1e-12
  // 同一点 or ほぼ同一点 → ズーム変更なしで center だけ固定
  if (Math.abs(dxu) < EPS && Math.abs(dyu) < EPS) {
    const opts = { center: [cx, cy] }
    animate ? map.easeTo({ ...opts, duration: 300 }) : map.jumpTo(opts)
    return
  }

  // 必要スケール（worldSize = 512 * 2^z、ピクセルで収まる条件）
  const needScaleX = (Math.abs(dxu) < EPS) ? Infinity : (halfW / (512 * Math.abs(dxu)))
  const needScaleY = (Math.abs(dyu) < EPS) ? Infinity : (halfH / (512 * Math.abs(dyu)))
  const needScale  = Math.min(needScaleX, needScaleY)

  const curZ = map.getZoom?.() ?? 16
  const zMax = (typeof maxZoom === 'number') ? maxZoom : (map.getMaxZoom?.() ?? 22)
  const zMin = (typeof minZoom === 'number') ? minZoom : (map.getMinZoom?.() ?? 0)

  // 収めるのに必要なズーム（ズームイン/アウト両方向OK）
  let zReq = Math.log2(Math.max(needScale, 1e-9))
  zReq = Math.min(zReq, zMax)
  zReq = Math.max(zReq, zMin)

  // 変更が微小なら何もしない（ちらつき防止）
  if (Math.abs(zReq - curZ) < hysteresis) {
    const opts = { center: [cx, cy] }
    animate ? map.easeTo({ ...opts, duration: 200 }) : map.jumpTo(opts)
    return
  }

  // ★ ここが変更点：ズームインも許可（以前は Math.min(curZ, zReq) でアウトのみだった）
  const targetZ = zReq
  const opts = { center: [cx, cy], zoom: targetZ }
  // animate ? map.easeTo({ ...opts, duration: 300, padding: 0 }) : map.jumpTo(opts)

  map.setZoom(targetZ)

}